import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip
} from '@chakra-ui/react'
import { StopRecording } from '@components/icons'
import { useRecording, useTranscription } from '@daily-co/daily-react'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useEffect, useState } from 'react'
import { useClinicianTelehealthSession } from '../context/clinician-telehealth-session.context'

import { AudioIndicator } from './audio-indicator'

export const ClinicianTranscriptionIndicator = () => {
  const [isStartingTranscription, setIsStartingTranscription] = useState(false)

  const { isTranscribing, startTranscription, stopTranscription } =
    useTranscription()
  const { recordingStartedAt, patient } = useClinicianTelehealthSession()
  const { startRecording, stopRecording } = useRecording()

  useEffect(() => {
    if (recordingStartedAt) {
      setIsStartingTranscription(true)
      setTimeout(() => {
        setIsStartingTranscription(false)
      }, 1500)
    }
  }, [recordingStartedAt])

  const onToggleTranscribe = useCallback(() => {
    if (isTranscribing) {
      stopTranscription()
      stopRecording()
    } else {
      setIsStartingTranscription(true)
      setTimeout(() => {
        setIsStartingTranscription(false)
      }, 1500)
      startTranscription({
        model: 'nova-2'
      })
      startRecording({ layout: { preset: 'audio-only' } })
    }
  }, [
    isTranscribing,
    stopTranscription,
    startTranscription,
    stopRecording,
    startRecording
  ])

  return (
    <Box
      position="absolute"
      top="0px"
      left="0px"
      right={recordingStartedAt ? undefined : '12px'}
      zIndex={100}
      transition="right 0.8s ease-in"
    >
      <motion.div
        layout
        transition={{
          layout: { duration: 0.8, type: 'spring', stiffness: 140, damping: 25 }
        }}
      >
        <HStack
          spacing="4px"
          bg="#282828"
          p="4px"
          borderRadius="8px"
          whiteSpace="nowrap"
          h="32px"
        >
          <AudioIndicator isTranscribing={isTranscribing} />
          <AnimatePresence>
            {!recordingStartedAt ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Text color="#757575" fontSize="12px">
                  Transcription will start automatically when you admit{' '}
                  {patient?.first_name}
                </Text>
              </motion.div>
            ) : // delay because it takes a second to start/resume the transcription
            isStartingTranscription ? (
              <Flex
                justify="center"
                align="center"
                w="24px"
                h="24px"
                ml="medium"
                mr="small"
              >
                <Spinner w="16px" h="16px" color="#68E19F" speed="0.6s" />
              </Flex>
            ) : isTranscribing ? (
              <Tooltip
                bg="primary"
                borderRadius="4px"
                p="xsmall"
                label={isTranscribing ? 'Stop transcript' : ''}
                placement="bottom-start"
                hasArrow
                display={isTranscribing ? 'flex' : 'none'}
              >
                <IconButton
                  _focus={{ outline: 'none' }}
                  _hover={{ bg: 'black' }}
                  onClick={onToggleTranscribe}
                  borderRadius="4px"
                  aria-label="Stop Transcription"
                  bg="transparent"
                  border="none"
                  size="sm"
                  h="24px"
                  w="24px"
                  minW="24px"
                  icon={<StopRecording width="16px" fill="#c9c9c9" />}
                />
              </Tooltip>
            ) : (
              <Button
                _focus={{ outline: 'none' }}
                _hover={{ bg: 'black' }}
                bg="transparent"
                h="24px"
                w="fit-content"
                onClick={onToggleTranscribe}
                borderRadius="4px"
                aria-label="Resume Transcription"
                color="#68E19F"
                fontSize="12px"
                p="4px"
              >
                Resume
              </Button>
            )}
          </AnimatePresence>
        </HStack>
      </motion.div>
    </Box>
  )
}
